import { api } from '@goaltree/backend/convex/_generated/api'
import { useConvexAuth, useMutation } from 'convex/react'
import { Globe } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LANG_STORAGE_KEY, SUPPORTED_LANGS } from '@/i18n'
import { optimisticSetLocale } from '@/lib/prefs-optimistic'

const LANG_LABELS: Record<string, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  pt: 'Português',
  ja: '日本語',
  zh: '中文',
}

export function LangSwitcher() {
  const { i18n: i18nInstance } = useTranslation()
  const [open, setOpen] = useState(false)
  const { isAuthenticated } = useConvexAuth()
  const setLocale = useMutation(api.preferences.setLocale).withOptimisticUpdate(optimisticSetLocale)

  function setLang(lang: string) {
    localStorage.setItem(LANG_STORAGE_KEY, lang)
    i18nInstance.changeLanguage(lang)
    if (isAuthenticated) void setLocale({ locale: lang })
    setOpen(false)
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex cursor-pointer items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground"
      >
        <Globe className="h-3.5 w-3.5" />
        {LANG_LABELS[i18nInstance.language] ?? i18nInstance.language}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-20 mt-1 min-w-[130px] overflow-hidden rounded-xl border border-border/60 bg-background shadow-lg">
            {SUPPORTED_LANGS.map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setLang(lang)}
                className={`flex w-full cursor-pointer items-center px-3 py-2 text-sm transition-colors hover:bg-muted ${
                  i18nInstance.language === lang
                    ? 'font-semibold text-foreground'
                    : 'text-muted-foreground'
                }`}
              >
                {LANG_LABELS[lang] ?? lang}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
